import {
  Autocomplete,
  Chip,
  FormHelperText,
  TextField,
  Tooltip,
  createFilterOptions,
} from "@mui/material";
import i18next from "i18next";
import { get as lodashGet, isEmpty } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import EAIcon from "components/common/EAIcon";
import useStyles from "../../style/js-style/containers/administration/rules/RuleFormsStyle";
import { useEffect } from "react";
import { useState } from "react";
import { theme } from "style/globalStyle";

const filterOptions = createFilterOptions({
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: "any",
  limit: 300,
});

const EAAutoCompleteInput = ({
  fieldName,
  options,
  placeholder,
  isRequired,
  multiple,
  variant = "standard",
  limitTags,
  disabled = false,
  freeSolo = false,
  clearPlaceHolderOnInput = false,
  maxValues = 100, //Max nb of tags the user can enter
  onChange = () => { },
  valueName = "value",
  defaultValue = null,
  style,
  disableClearable = false,
  errors = null,
  formMethodsProps,
  triggerOnContinue = false,
  validate = () => { },
  values = null
}) => {
  const { classes } = useStyles();
  const formContext = useFormContext();
  const formMethods = formMethodsProps || formContext;

  const getValueFromField = (values) => {
    if (isEmpty(options)) return [];
    if (multiple) {
      if (!values) return [];
      if (!Array.isArray(values)) return [];
      return values.map((value) => {
        let result;
        if (typeof value === 'object') {
          result = options.find((val) => val[valueName] === value[valueName]);
        } else {
          result = options.find((val) => val[valueName] === value);
        }
        return result === undefined ? null : result;
      });
    } else {
      if (values === null) return null;
      const result = options.find((val) => val[valueName] === values);
      return result === undefined ? null : result;
    }
  };

  const displayPlaceHolder = () => {
    const val = formMethods.getValues(fieldName);
    if (!clearPlaceHolderOnInput || val?.length === 0) {
      return placeholder;
    } else {
      return "";
    }
  }

  // Check de la valeur dès le rendu
  useEffect(() => {
    triggerOnContinue && formMethods.trigger(fieldName);
    // eslint-disable-next-line
  }, [triggerOnContinue]);

  return (
    <div>
      {options && (
        <Controller
          control={formMethods.control}
          name={fieldName}
          defaultValue={defaultValue ? defaultValue : multiple ? [] : null}
          rules={{
            validate: (value) => {
              const validation = validate(value);
              if (validation) {
                return validation;
              } else if (isRequired) {
                const isEmpty =
                  value === null || value === undefined || value.length === 0;
                if (isEmpty) {
                  return i18next.t("infos.required");
                } else {
                  formMethods.clearErrors(fieldName);
                }
              }
            },
          }}
          render={({ field }) => (
            <Autocomplete
              id={`${fieldName}-autocomplete-id`}
              {...field}
              disableCloseOnSelect={multiple}
              value={values || getValueFromField(field["value"])}
              multiple={multiple}
              autoComplete
              options={options}
              clearOnBlur={true}
              freeSolo={freeSolo}
              noOptionsText={i18next.t(`tableLabels.noMatch`)}
              openText={i18next.t(`open`)}
              clearText={i18next.t(`tableLabels.clear`)}
              clearIcon={multiple ? null : undefined}
              disableClearable={multiple || disableClearable}
              closeText={i18next.t(`cancel`)}
              filterOptions={filterOptions}
              autoHighlight
              disabled={disabled}
              className={classes.autocompleteWrapper}
              sx={{
                "& .MuiFormControl-root": {
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "no-wrap !important",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  border: "none",
                },
                ...style
              }}
              isOptionEqualToValue={(option, value) => {
                return value ? option[valueName] === value[valueName] : false;
              }}
              onKeyDown={() => {
                const val = formMethods.getValues(fieldName);
                if (maxValues && val?.length >= maxValues) {
                  formMethods.setError(fieldName, {
                    message: i18next.t('infos.MAX_TAGS_ERROR', { nbTag: maxValues }),
                  })
                }
              }}
              onChange={(event, target) => {
                let value;
                if (multiple) {
                  value = target?.map((obj) => {
                    return obj[valueName] || obj;
                  })
                } else {
                  value = target?.[valueName] || target;
                }
                const valuesIsArray = Array.isArray(value);
                if (maxValues && (valuesIsArray ? value.length <= maxValues : true)) {
                  formMethods.setValue(fieldName, value);
                  formMethods.trigger(fieldName);
                  onChange(target);
                }
                formMethods.clearErrors(fieldName);
                triggerOnContinue && formMethods.trigger(fieldName);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant={variant}
                  error={
                    !!lodashGet(formMethods.formState.errors, `${fieldName}`) || !!errors
                  }
                  placeholder={displayPlaceHolder()}
                  className={classes.textfield}
                  sx={{
                    "& .MuiOutlinedInput-root": { border: "none", },
                    input: {
                      "&::placeholder": {
                        color: theme.palette.grey.grey400 + " !important",
                        fontSize: "14px !important",
                        fontWeight: 400 + " !important",
                        fontStyle: "normal !important"
                      }
                    }
                  }}
                />
              )}
              renderTags={(values, getTagProps) => (
                <EAAutoCompleteTags
                  values={values}
                  onDelete={(index) => getTagProps({ index }).onDelete()}
                  limitTags={limitTags}
                />
              )}
              renderOption={({ key: _1, ...props }, option, { inputValue }) => {
                const matches = match(option.label, inputValue, {
                  insideWords: true,
                });
                const parts = parse(option.label, matches);
                return (
                  <li key={option[valueName] || option} {...props}>
                    <div>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              }}
            />
          )}
        />
      )}
      {(!!lodashGet(formMethods.formState.errors, `${fieldName}`) || !!errors) && (
        <FormHelperText className="Mui-error">
          {lodashGet(formMethods.formState.errors, `${fieldName}.message`) || errors.message}
        </FormHelperText>
      )}
    </div>
  );
};

const EAAutoCompleteTags = ({
  values = [],
  onDelete = () => { },
  limitTags,
  // add multiple ?
}) => {
  const { classes } = useStyles();
  const [displayedTags, setDisplayedTags] = useState();
  const [hiddenTags, setHiddenTags] = useState();
  const [hiddenCounter, setHiddenCounter] = useState();

  useEffect(() => {
    if (limitTags) {
      const tagsToDisplay = values?.slice(0, limitTags);
      setDisplayedTags(tagsToDisplay);
      setHiddenTags(values?.slice(limitTags, values.length));
      setHiddenCounter(values?.length - tagsToDisplay?.length);
    } else {
      setDisplayedTags(values);
    }
  }, [values, limitTags]);

  return (
    <div className={classes.tagsWrapper}>
      {hiddenCounter > 0 && (
        <Tooltip
          title={
            <ul style={{ paddingLeft: "12px", margin: "4px" }}>
              {hiddenTags?.map((tag) => (
                <li key={tag.label}>{tag.label}</li>
              ))}
            </ul>
          }
        >
          <div className={classes.tagsCounter}>+{hiddenCounter}</div>
        </Tooltip>
      )}
      {displayedTags?.map((objValue, index) => {
        return (
          // multiple &&
          objValue && (
            <Chip
              key={"autocomplete-" + index}
              label={objValue.label}
              value={objValue.value}
              onDelete={() => onDelete(index)}
              deleteIcon={
                <EAIcon icon="cross" className={classes.chipCrossIcon} />
              }
              sx={{
                borderRadius: 0,
                backgroundColor: "#F2F3F7",
                color: "#535362",
                padding: 0,
                fontSize: "12px",
                fontWeight: 500,
                height: "auto",
                border: 0,
                maxWidth: "200px",
                margin: "7px 4px",
                "& .MuiChip-label": {
                  paddingLeft: "4px",
                  paddingRight: "12px",
                },
              }}
            />
          )
        );
      })}
    </div>
  );
};

export default EAAutoCompleteInput;
